import { t, Trans, Plural } from '@lingui/macro';
import React, { useEffect, useRef, useState } from 'react';

import { CommonFaceResponse } from '@api/v4/private/people_tagging/faces';
import { BFLoader } from '@components/common/loader/main';
import Tooltip from '@components/common/tooltip/main';
import { PrimaryButton, TertiaryButton } from '@components/library/button/index';

interface BulkPeopleContentProps {
  assetCount: number;
  handleClose: () => void;
  handleRemoveFace: (faceIds: string[]) => void;
  handleSave: () => void | Promise<void>;
  isLoadingFaces: boolean;
  isSubmitting: boolean;
  removedFaceIdsCount: number;
  visibleFaces: CommonFaceResponse[];
}

// Component that shows tooltip only when name is truncated
const PersonNameWithTooltip: React.FC<{ name: string; faceId: string }> = ({ name, faceId }) => {
  const nameRef = useRef<HTMLParagraphElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = (): void => {
      if (nameRef.current && name) {
        const element = nameRef.current;

        // Check if text is actually truncated by comparing scroll width to client width
        setIsTextTruncated(element.scrollWidth > element.clientWidth);
      } else {
        setIsTextTruncated(false);
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(checkTruncation);
    }, 0);

    // Recheck on window resize to handle responsive layout changes
    window.addEventListener('resize', checkTruncation);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [name]);

  return (
    <div className="person-name-wrapper">
      {isTextTruncated ? (
        <Tooltip
          className="person-name-tooltip-wrapper"
          placement="top"
          tooltipContent={name}
          tooltipId={`bulk-person-name-tooltip-${faceId}`}
          type="dark"
        >
          <p ref={nameRef} className="person-name">
            {name}
          </p>
        </Tooltip>
      ) : (
        <p ref={nameRef} className="person-name">
          {name}
        </p>
      )}
    </div>
  );
};

const BulkPeopleContent: React.FC<BulkPeopleContentProps> = ({
  assetCount,
  handleClose,
  handleRemoveFace,
  handleSave,
  isLoadingFaces,
  isSubmitting,
  removedFaceIdsCount,
  visibleFaces,
}) => (
  <div className="modal-content-wrapper bulk-people-modal">
    <div className="modal-content-wrapper__body">
      {isLoadingFaces ? (
        <BFLoader />
      ) : visibleFaces.length === 0 ? (
        <p className="no-common-faces">
          <Trans>No common faces found for the selected assets.</Trans>
        </p>
      ) : (
        <>
          <h3 className="body-title">
            <Plural
              one={
                <Trans>
                  Remove people tags from <strong>common</strong> faces for <strong>{assetCount} asset</strong>
                </Trans>
              }
              other={
                <Trans>
                  Remove people tags from <strong>common</strong> faces for <strong>{assetCount} assets</strong>
                </Trans>
              }
              value={assetCount}
            />
          </h3>
          <div className="people-grid">
            {visibleFaces.map((face) => (
              <div key={face.face_ids[0]} className="person-entry">
                <div className="person-image-container">
                  <img
                    alt={face.identity_name}
                    className="person-image"
                    src={face.identity_thumbnail_url}
                  />
                  <button
                    aria-label={t`Remove ${face.identity_name}`}
                    className="remove-person-button"
                    onClick={() => handleRemoveFace(face.face_ids)}
                    type="button"
                  >
                    <span aria-hidden="true" className="bff-close" />
                  </button>
                </div>
                <PersonNameWithTooltip faceId={face.face_ids[0]}  name={face.identity_name} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
    <div className="button-container">
      <TertiaryButton
        className="t-close-modal"
        disabled={isSubmitting || isLoadingFaces}
        onClick={handleClose}
        size="small"
      >
        <Trans>Close</Trans>
      </TertiaryButton>
      <PrimaryButton
        className="t-confirm-modal"
        disabled={removedFaceIdsCount === 0 || isLoadingFaces}
        isLoading={isSubmitting}
        onClick={() => void handleSave()}
        size="small"
      >
        <Trans>Save</Trans>
      </PrimaryButton>
    </div>
  </div>
);

export default BulkPeopleContent;
